import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { useListServiceProviders } from 'src/features/serviceProvider/api/useListServiceProviders';
import CreateServiceProviderFormModal from 'src/features/serviceProvider/components/form/CreateServiceProviderFormModal';
import CellSettingsActions from 'src/features/serviceProvider/components/table/CellServiceProviderActions';
import { organisationMessages } from 'src/features/serviceProvider/utils/messages';
import { ServiceProvider } from 'src/features/serviceProvider/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalMessages } from 'src/utils/messages';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'name' };
const columnHelper = createColumnHelper<ServiceProvider>();

function ServiceProviders() {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const manageServiceProviders = useHasPermissions([PERMISSION.MANAGE_SERVICE_PROVIDERS]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, parkingId, queryPaginationOptions],
    );

    const { data, isLoading, isError } = useListServiceProviders(payload);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(organisationMessages.organisations));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const columns = React.useMemo(
        () => [
            columnHelper.accessor('name', {
                header: DataTableColumnHeader,
                enableSorting: true,
                meta: {
                    title: formatMessage(generalMessages.name),
                },
            }),
            columnHelper.accessor('uic', {
                header: DataTableColumnHeader,
                enableSorting: true,
                enableHiding: true,
                meta: {
                    title: formatMessage(organisationMessages.uic),
                },
            }),
            columnHelper.accessor('tax_number', {
                header: DataTableColumnHeader,
                enableSorting: true,
                enableHiding: true,
                meta: {
                    title: formatMessage(organisationMessages.taxNumber),
                },
            }),
            columnHelper.accessor('legal_address', {
                header: DataTableColumnHeader,
                enableSorting: true,
                enableHiding: true,
                meta: {
                    title: formatMessage(organisationMessages.address),
                },
            }),
            columnHelper.accessor('representative', {
                header: DataTableColumnHeader,
                enableSorting: true,
                enableHiding: true,
                meta: {
                    title: formatMessage(organisationMessages.legalRepresentative),
                },
            }),
            ...(manageServiceProviders
                ? [
                      columnHelper.display({
                          id: 'actions',
                          header: DataTableColumnHeader,
                          cell: CellSettingsActions,
                          maxSize: 45,
                      }),
                  ]
                : []),
        ],
        [formatMessage, manageServiceProviders],
    );

    return (
        <>
            <DataTable<ServiceProvider, any>
                isError={isError}
                isLoading={isLoading}
                columns={columns}
                data={data}
                search={search}
                filters={filters}
                sorting={sorting}
                pagination={pagination}
                semantics={{
                    title: formatMessage(organisationMessages.organisations),
                    entryName: formatMessage(organisationMessages.organisation).toLowerCase(),
                    entryNames: formatMessage(organisationMessages.organisations).toLowerCase(),
                    placeholder: formatMessage(organisationMessages.organisation),
                }}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    ...(manageServiceProviders && { newEntry: CreateServiceProviderFormModal }),
                }}
            />
        </>
    );
}

export default ServiceProviders;
